import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Asignaciones, AsignacionesPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchAsignaciones = (params?: GetAsignacionesParams) => {
  return useQuery({
    queryKey: ['adminAsignaciones', ...Object.values(params || {})],
    queryFn: async () => {
      return getAsignacioness(params);
    },
  });
};

export const useGetAsignaciones = (id: string) => {
  return useQuery({
    queryKey: ['adminAsignaciones', id],
    queryFn: async () => {
      return getAsignaciones(id);
    },
  });
};

export const useCreateAsignaciones = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createAsignaciones,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asignaciones'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Asignación creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateAsignaciones = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateAsignaciones,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asignaciones'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Asignaciones actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
///* axios
export type GetAsignacionesParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
  fecha_asignacion_before?: string;
  fecha_devolucion_after?: string;
  activo?: string;
};
export type CreateAsignacionesParams = Omit<
  Asignaciones,
  'id_Asignaciones'
>;
export type UpdateAsignacionesParams = {
  id: string;
  data: CreateAsignacionesParams;
};

export const getAsignacioness = async (params?: GetAsignacionesParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<AsignacionesPagingRes>(
    `${urlBackend}api/v1/asignacionactivo/?${queryParams}`,
    true
  );
};

export const getAsignaciones = async (id: string) => {
  try {
    return await get<Asignaciones>(
      `${urlBackend}api/v1/asignacionactivo/${id}`,
      true
    );
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createAsignaciones = async (data: CreateAsignacionesParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Asignaciones>(`${urlBackend}api/v1/asignacionactivo/`, data, true);
};

export const updateAsignaciones = async ({
  id,
  data,
}: UpdateAsignacionesParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<Asignaciones>(`${urlBackend}api/v1/asignacionactivo/${id}/`, data, true);
};
